import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export interface Organization {
  organization_id: string;
  name: string;
  contactEmail: string;
  contactPhone?: string;
  website?: string;
  chat_branding_id?: string | null;
  chat_branding_title?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateOrganizationDto {
  name: string;
  contactEmail: string;
}

export interface UpdateOrganizationDto {
  name?: string;
  contactEmail?: string;
}

export const organizationApi = createApi({
  reducerPath: "organizationApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["Organization"],
  endpoints: (builder) => ({
    getOrganizations: builder.query<Response<Organization[]>, void>({
      query: () => ({
        url: "organization/list",
        method: "GET",
      }),
      transformResponse: (response: unknown) => {
        const r = response as { data?: Organization[] } | Organization[];
        if (Array.isArray(r)) {
          return { success: true, code: 200, message: "", data: r };
        }
        if (r && typeof r === "object" && "data" in r && Array.isArray(r.data)) {
          return { success: true, code: 200, message: "", data: r.data };
        }
        return response as Response<Organization[]>;
      },
      providesTags: ["Organization"],
    }),
    getOrganization: builder.query<Response<Organization>, string>({
      query: (id) => ({
        url: `organization/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown) => {
        const r = response as { data?: Organization } | Organization;
        if (r && typeof r === "object") {
          if ("data" in r && r.data) {
            return { success: true, code: 200, message: "", data: r.data };
          }
          if ("organization_id" in r) {
            return { success: true, code: 200, message: "", data: r as Organization };
          }
        }
        return { success: true, code: 200, message: "", data: null as unknown as Organization };
      },
      providesTags: (_result, _error, id) => [{ type: "Organization", id }],
    }),
    createOrganization: builder.mutation<Response<Organization>, CreateOrganizationDto>({
      query: (data) => ({
        url: "organization/create",
        method: "POST",
        data,
      }),
      transformResponse: (response: unknown) => {
        const r = response as { data?: Organization } | Organization;
        if (r && typeof r === "object") {
          if ("data" in r && r.data) {
            return { success: true, code: 200, message: "", data: r.data };
          }
          if ("organization_id" in r) {
            return { success: true, code: 200, message: "", data: r as Organization };
          }
        }
        return { success: true, code: 200, message: "", data: null as unknown as Organization };
      },
      invalidatesTags: ["Organization"],
    }),
    updateOrganization: builder.mutation<
      Response<Organization>,
      { id: string; data: UpdateOrganizationDto }
    >({
      query: ({ id, data }) => ({
        url: `organization/${id}`,
        method: "PATCH",
        data,
      }),
      transformResponse: (response: unknown) => {
        const r = response as { data?: Organization } | Organization;
        if (r && typeof r === "object") {
          if ("data" in r && r.data) {
            return { success: true, code: 200, message: "", data: r.data };
          }
          if ("organization_id" in r) {
            return { success: true, code: 200, message: "", data: r as Organization };
          }
        }
        return { success: true, code: 200, message: "", data: null as unknown as Organization };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: "Organization", id }, "Organization"],
    }),

    mapOrganizationChatBranding: builder.mutation<
      Response<{
        chat_branding_id: string;
        organization_id: string;
        organization_name: string;
        contact_email: string;
      }>,
      { organizationId: string; chat_branding_id: string }
    >({
      query: ({ organizationId, chat_branding_id }) => ({
        url: `organization/${organizationId}/map-chat-branding`,
        method: "POST",
        data: { chat_branding_id },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: "Organization", id: organizationId },
        "Organization",
      ],
    }),
  }),
});

export const {
  useGetOrganizationsQuery,
  useGetOrganizationQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useMapOrganizationChatBrandingMutation,
} = organizationApi;
